import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { Bus } from "../models/bus";
import { BusStop } from "../models/bus-stop";
import { BusStopSchema } from "../models/bus-stop-schema";
import { DefaultValue } from "../models/default-value";
import { Level } from "../models/level";
import { MailContext } from "../models/mail-context";
import { PaymentSource } from "../models/payment-source";
import { Sport } from "../models/sport";
import { Template } from "../models/template";
import { TransportSchema } from "../models/transport-schema";
import { User } from "../models/user";
import { ageRangeToDB, ageRangeToView, timeToDB, timeToView } from "../shared/utils";

@Injectable({ providedIn: 'root' })
export class SettingsService {
    private readonly baseUrl = `${environment.api}/settings`;

    constructor(private http: HttpClient) { }
    public all = async (): Promise<{
        sports: Sport[], levels: Level[], defaultValues: DefaultValue[], buses: Bus[], users: User[], busStops: BusStop[],
        transportSchemas: TransportSchema[], templates: Template[], contexts: MailContext[], payments: PaymentSource[]
    }> => {
        const result = await firstValueFrom(this.http.get<{
            sports: Sport[], levels: Level[], defaultValues: DefaultValue[], buses: Bus[], users: User[], busStops: BusStop[],
            transportSchemas: TransportSchema[], templates: Template[], contexts: MailContext[], payments: PaymentSource[]
        }>(`${this.baseUrl}/all`));
        for (const defaultValue of result.defaultValues) {
            defaultValue.ageRange = ageRangeToView((defaultValue as any).minAge, (defaultValue as any).maxAge);
        }
        result.transportSchemas.map(s => this.transportSchemaToView(s));
        return result;
    }

    public sportAdd = async (data: any): Promise<Sport> => {
        return await firstValueFrom(this.http.post<Sport>(`${this.baseUrl}/sport/add`, { data }));
    }
    public sportUpdate = async (data: any): Promise<Sport> => {
        return await firstValueFrom(this.http.put<Sport>(`${this.baseUrl}/sport/update`, { data }));
    }

    public levelAdd = async (data: any): Promise<Level> => {
        return await firstValueFrom(this.http.post<Level>(`${this.baseUrl}/level/add`, { data }));
    }
    public levelUpdate = async (data: any): Promise<Level> => {
        return await firstValueFrom(this.http.put<Level>(`${this.baseUrl}/level/update`, { data }));
    }
    public levelDelete = async (level: string) => {
        return await firstValueFrom(this.http.delete<boolean>(`${this.baseUrl}/level/delete/${level}`))
    }
    public levelCanBeDeleted = async (level: string): Promise<boolean> => {
        const result = await firstValueFrom(this.http.get<boolean>(`${this.baseUrl}/level/can-be-deleted/${level}`))
        return result.toString() === "true";
    }
    public levelSetOrder = async (levels: Level[]): Promise<Level[]> => {
        const data = levels.map(l => ({ id: l.id, tc: l.timeCreate }))
        return await firstValueFrom(this.http.post<Level[]>(`${this.baseUrl}/level/set-order`, { data }))
    }

    public defaultValueAdd = async (data: any): Promise<DefaultValue> => {
        data = { ...data, ...ageRangeToDB(data.ageRange) }
        const result = await firstValueFrom(this.http.post<DefaultValue>(`${this.baseUrl}/defaultValue/add`, { data }));
        result.ageRange = ageRangeToView((result as any).minAge, (result as any).maxAge);
        return result;
    }
    public defaultValueUpdate = async (data: any): Promise<DefaultValue> => {
        data = { ...data, ...ageRangeToDB(data.ageRange) }
        const result = await firstValueFrom(this.http.put<DefaultValue>(`${this.baseUrl}/defaultValue/update`, { data }));
        result.ageRange = ageRangeToView((result as any).minAge, (result as any).maxAge);
        return result;
    }

    public busAdd = async (data: any): Promise<Bus> => {
        const result = await firstValueFrom(this.http.post<Bus>(`${this.baseUrl}/bus/add`, { data }));
        return result;
    }
    public busUpdate = async (data: any): Promise<Bus> => {
        const result = await firstValueFrom(this.http.put<Bus>(`${this.baseUrl}/bus/update`, { data }));
        return result;
    }

    public busStopAdd = async (data: any): Promise<BusStop> => {
        const result = await firstValueFrom(this.http.post<BusStop>(`${this.baseUrl}/busStop/add`, { data }));
        return result;
    }
    public busStopUpdate = async (data: any): Promise<BusStop> => {
        const result = await firstValueFrom(this.http.put<BusStop>(`${this.baseUrl}/busStop/update`, { data }));
        return result;
    }

    public transportSchemaAdd = async (data: any): Promise<TransportSchema> => {
        const result = await firstValueFrom(this.http.post<TransportSchema>(`${this.baseUrl}/transportSchema/add`, { data }));
        return result;
    }
    public transportSchemaUpdate = async (data: any): Promise<TransportSchema> => {
        data = this.transportSchemaToDb(data);
        let result = await firstValueFrom(this.http.put<TransportSchema>(`${this.baseUrl}/transportSchema/update`, { data }));
        result = this.transportSchemaToView(result);
        for (const stop of result.stops ?? []) {
            stop.timeMorning = timeToView(stop.timeMorning?.toString());
            stop.timeEvening = timeToView(stop.timeEvening?.toString());
        }
        return result;
    }
    public busStopSchemaAdd = async (data: any): Promise<BusStopSchema> => {
        data = this.busStopSchemaToDb(data);
        let result = await firstValueFrom(this.http.post<BusStopSchema>(`${this.baseUrl}/busStopSchema/add`, { data }));
        result = this.busStopSchemaToView(result);
        return result;
    }
    public busStopSchemaUpdate = async (data: any): Promise<BusStopSchema> => {
        data = this.busStopSchemaToDb(data);
        let result = await firstValueFrom(this.http.put<BusStopSchema>(`${this.baseUrl}/busStopSchema/update`, { data }));
        result = this.busStopSchemaToView(result);
        return result;
    }
    public busStopSchemaDelete = async (id: string): Promise<BusStopSchema> => {
        let result = await firstValueFrom(this.http.delete<BusStopSchema>(`${this.baseUrl}/busStopSchema/delete/${id}`));
        result = this.busStopSchemaToView(result);
        return result;
    }

    public emailTemplateAdd = async (data: any): Promise<Template> => {
        const result = await firstValueFrom(this.http.post<Template>(`${this.baseUrl}/emailTemplate/add`, { data }));
        return result;
    }
    public emailTemplateUpdate = async (data: any): Promise<Template> => {
        const result = await firstValueFrom(this.http.put<Template>(`${this.baseUrl}/emailTemplate/update`, { data }));
        return result;
    }

    public paymentSourceAdd = async (data: any): Promise<PaymentSource> => {
        const result = await firstValueFrom(this.http.post<PaymentSource>(`${this.baseUrl}/paymentSource/add`, { data }));
        return result;
    }
    public paymentSourceUpdate = async (data: any): Promise<PaymentSource> => {
        const result = await firstValueFrom(this.http.put<PaymentSource>(`${this.baseUrl}/paymentSource/update`, { data }));
        return result;
    }

    private transportSchemaToView = (transportSchema: TransportSchema) => {
        transportSchema.stops = transportSchema.stops.map(s => this.busStopSchemaToView(s));
        return transportSchema;
    }
    private transportSchemaToDb = (data: any) => {
        data.stops = data.stops.map((s: any) => this.busStopSchemaToDb(s));
        return data;
    }
    private busStopSchemaToView = (stop: BusStopSchema) => {
        stop.timeMorning = timeToView(stop.timeMorning?.toString());
        stop.timeEvening = timeToView(stop.timeEvening?.toString());
        return stop;
    }
    private busStopSchemaToDb = (data: any) => {
        data.timeMorning = timeToDB(data.timeMorning);
        data.timeEvening = timeToDB(data.timeEvening);
        return data;
    }
}
